import { getCookie } from '@visa/lib-cookie';
import {
  cookieExist,
  getSsiStatus,
  getUsernameFromCookie,
  getUserType
} from '../../src-i-adapter/helpers/cookie-manager';
import getIncomingTrafficSource from '../../src-i-adapter/helpers/get-incoming-traffic-source';
import { srcInitiatorIdMap } from '../../src-i-adapter/helpers/gtm';
import { CAMPAIGN_TRACKING_COOKIE } from '../constants/campaign';
import { AMEX, MA, VISA } from '../constants/common-enums';
import { DEFAULT_VALUE, TRANSACTION_TYPE_ENUM } from '../constants/external-sdk-maps';
import { SYS_SYNC_GTM_DATA_EVENT } from '../constants/gtm-events';
import {
  alwaysRememberIsTrue,
  getSsiEnabledCookieValues,
  getSsiDeclinedCookieValues,
  isUserRecognized
} from '../lib/ssi-erm/ssi-erm-cookie-manager';
import maskEmail from './mask-email';
import PostMessage from './post-message';

const FLOW = {
  INO: 'INO'
};

const REMEMBER_ME_TYPE = {
  ERM: 'ERM',
  LEGACY: 'legacy'
};

const STAY_SIGNED_IN = {
  OPTED_IN: 'OPTED_IN',
  OPTED_OUT: 'OPTED_OUT'
};

const TRAFFIC_SOURCE = {
  WEB: 'web'
};

const UNDEFINED = 'undefined';

const USER_FLOW = {
  EXISTING: 'existing',
  NEW: 'new'
};

const USER_TYPE = {
  RECOGNIZED: 'Recognized',
  RECOGNIZED_USERNAME: 'Recognized-Username',
  UNRECOGNIZED: 'Unrecognized'
};

export const pushGtmData = data => {
  try {
    if (window.dataLayer) {
      window.dataLayer.push(data);
    }
  } catch (e) {
    console.error(e); // eslint-disable-line
  }
};

const trackNonVisaSRCiCustomAttributes = config => {
  const correlation_id = config && config.correlation_id;
  const protocol = window.location.protocol.slice(0, -1);
  const isAlwaysRemember = alwaysRememberIsTrue();
  const isUserRecognizedBool = isUserRecognized();
  const paymentRequest =
    config && config.vInitRequest && config.vInitRequest.paymentRequest
      ? config.vInitRequest.paymentRequest
      : {};
  const xo_visitid = config && config.xo_visitid;

  let userType = USER_TYPE.UNRECOGNIZED;
  if (isUserRecognizedBool) {
    if (isAlwaysRemember) {
      userType = USER_TYPE.RECOGNIZED_USERNAME;
    } else {
      userType = USER_TYPE.RECOGNIZED;
    }
  }

  let staySignedIn = UNDEFINED;
  if (getSsiEnabledCookieValues()) {
    staySignedIn = STAY_SIGNED_IN.OPTED_IN;
  } else if (getSsiDeclinedCookieValues()) {
    staySignedIn = STAY_SIGNED_IN.OPTED_OUT;
  }

  const gtmData = {
    auth_type: UNDEFINED,
    browser_protocol: protocol,
    cart_amount: paymentRequest.total || UNDEFINED,
    correlation_id,
    currency_cd: paymentRequest.currencyCode || UNDEFINED,
    flow: FLOW.INO,
    incoming_traffic_source: TRAFFIC_SOURCE.WEB,
    // VCO-13141 - default to ERM is wrong in new-user flow
    // TODO: refactor to common lib as this helper is essentially a copy/paste of src-widget/../dcf/helpers/gtm.ts
    rememberme_type:
      isUserRecognizedBool && isAlwaysRemember ? REMEMBER_ME_TYPE.ERM : REMEMBER_ME_TYPE.LEGACY,
    stay_signed_in: staySignedIn,
    user_type: userType,
    username_remembered: isAlwaysRemember,
    xo_visitid
  };

  pushGtmData(gtmData);

  return gtmData;
};

const trackTransactionAndOrderType = config => {
  let gtmData;

  if (config) {
    gtmData = {
      order_type: config.orderType || 'undefined',
      transaction_type: TRANSACTION_TYPE_ENUM.includes(config.transactionType)
        ? config.transactionType
        : DEFAULT_VALUE.TRANSACTION_TYPE
    };
  } else {
    gtmData = {
      order_type: 'undefined',
      transaction_type: DEFAULT_VALUE.TRANSACTION_TYPE
    };
  }

  pushGtmData(gtmData);
};

const trackCampaignCookieAttributes = () => {
  const campaignCodeCookie = JSON.parse(getCookie(CAMPAIGN_TRACKING_COOKIE) || '{}');

  pushGtmData({
    campaign_flow: campaignCodeCookie.niflow ?? null,
    campaign_logo: campaignCodeCookie.nicmp ?? null,
    campaign_marketing_channel: campaignCodeCookie.nichn ?? null,
    campaign_name: campaignCodeCookie.nicmpg ?? null,
    campaign_partner_id: campaignCodeCookie.nicaller ?? null,
    campaign_placement_id: campaignCodeCookie.niplc ?? null,
    campaign_portfolio: campaignCodeCookie.niptf ?? null,
    campaign_segment: campaignCodeCookie.niseg ?? null,
    campaign_site_id: campaignCodeCookie.nisite ?? null
  });
};

export const parseGetSrcProfileIdToken = token => {
  try {
    return typeof token === 'string' ? JSON.parse(atob(token.split('.')[1])) : {};
  } catch (err) {
    console.error(err); // eslint-disable-line no-console
    return {};
  }
};

const getBaseSetIntentProps = (config = {}, isVisaSrci) => {
  const srcInitiatorId = !isVisaSrci ? srcInitiatorIdMap[(config?.apikey)] || 'undefined' : VISA;

  return {
    api_key: config?.apikey ?? 'undefined',
    api_name: 'account/ARMGUID/intent',
    browser_protocol: window.location.protocol.split(':')[0],
    channel: 'merchant',
    correlation_id: config?.correlationId ?? 'undefined',
    experience_owner: 'SRCi',
    external_client_id: config?.merchantConfig?.externalClientId ?? 'undefined',
    flow: 'SRC',
    incoming_traffic_source: getIncomingTrafficSource(),
    merchant_country_code:
      config?.merchantConfig?.merchantCountryCode ??
      config?.dpaTransactionOptions?.merchantCountryCode ??
      'undefined',
    merchant_name:
      config?.merchantConfig?.logo?.displayName ??
      config?.merchantConfig?.displayName ??
      'undefined',
    merchant_site: config?.vInitRequest?.parentUrl ?? 'undefined',
    rememberme_type: cookieExist('ermEnabled') ? 'ERM' : 'legacy',
    src_initiator_id: srcInitiatorId,
    stay_signed_in: getSsiStatus(),
    user_type: getUserType(),
    username_remembered: getCookie('alwaysRemember') === 'true',
    xo_visitid: config?.xo_visitid ?? config?.visitId ?? 'undefined'
  };
};

let srcEmail = 'undefined';
const trackSetIntentCalled = (config = {}, DcfLaunchData, isRequest, isVisaSrci) => {
  const idTokens = DcfLaunchData?._customProps?.idTokens;
  let parsedToken;
  if (Array.isArray(idTokens) || (typeof idTokens === 'string' && idTokens.length > 0)) {
    let token = idTokens;
    if (Array.isArray(idTokens)) {
      token = idTokens[0]?.token ?? idTokens[0];
    }

    parsedToken = parseGetSrcProfileIdToken(token);
  }

  srcEmail =
    parsedToken?.src_email_mask ??
    maskEmail(DcfLaunchData?._customProps?.userInfo?.email) ??
    getUsernameFromCookie() ??
    'undefined';
  const eventName = 'Intent initiated';

  const label = `${eventName}-${isRequest ? 'Request' : 'Response'} API ${
    isRequest ? 'Pending' : 'Success'
  }`;
  pushGtmData({
    event: eventName,
    event_action: eventName,
    event_category: eventName,
    event_label: label,
    src_email_mask: srcEmail,
    ...getBaseSetIntentProps(config, isVisaSrci)
  });
};

const getBaseTrackRecognizedProps = (config = {}) => {
  return {
    api_key: config?.apikey,
    browser_protocol: window.location.protocol.split(':')[0],
    channel: 'merchant',
    correlation_id: config?.correlationId ?? 'undefined',
    experience_owner: 'SRCi',
    external_client_id: config?.merchantConfig?.externalClientId ?? 'undefined',
    flow: 'SRC',
    incoming_traffic_source: getIncomingTrafficSource(),
    merchant_country_code:
      config?.merchantConfig?.merchantCountryCode ??
      config?.dpaTransactionOptions?.merchantCountryCode ??
      'undefined',
    merchant_name:
      config?.merchantConfig?.logo?.displayName ??
      config?.merchantConfig?.displayName ??
      'undefined',
    merchant_site: config?.merchantConfig?.websiteUrl ?? 'undefined',
    rememberme_type: cookieExist('ermEnabled') ? 'ERM' : 'legacy',
    src_email_mask: getUsernameFromCookie(),
    src_initiator_id: srcInitiatorIdMap[(config?.apikey)] || 'undefined',
    src_system_name: srcInitiatorIdMap[(config?.apikey)] || VISA,
    stay_signed_in: getSsiStatus(),
    user_type: getUserType(),
    username_remembered: getCookie('alwaysRemember') === 'true',
    xo_visitid: config?.xo_visitid ?? config?.visitId ?? 'undefined'
  };
};

const convertIssToCardBrand = (iss = '') => {
  let srcSysName = 'undefined';
  if (iss.toLowerCase().includes('americanexpress')) {
    srcSysName = AMEX;
  } else if (iss.toLowerCase().includes('mastercard')) {
    srcSysName = MA;
  }

  return srcSysName;
};

const trackVisaRecognizedUser = ({ accessToken = '', config = {}, initData = {} } = {}) => {
  if (!initData.visaSrci) {
    const baseTrackRecognizedProps = {
      ...getBaseTrackRecognizedProps(config),
      auth_provider: 'undefined',
      nvr: false,
      user_type_identification: 'Recognized user',
      vat: Boolean(accessToken),
      vr: true
    };
    const eventName = 'User type identification';
    const gtmData = {
      api_name: 'isRecognized',
      event: eventName,
      event_action: eventName,
      event_label: `${eventName}-API`,
      ...baseTrackRecognizedProps
    };

    pushGtmData({
      ...gtmData
    });

    PostMessage.send({
      data: {
        ...baseTrackRecognizedProps
      },
      type: SYS_SYNC_GTM_DATA_EVENT
    });
  }
};

const trackNonVisaRecognizedUser = ({ config = {}, idTokens = [], initData = {} } = {}) => {
  if (!initData.visaSrci) {
    if (
      Array.isArray(idTokens?.idTokens) ||
      Array.isArray(idTokens) ||
      (typeof idTokens === 'string' && idTokens.length > 0)
    ) {
      let token = idTokens;
      if (Array.isArray(idTokens)) {
        token = idTokens[0];
      } else if (Array.isArray(idTokens.idTokens)) {
        token = idTokens.idTokens[0];
      }

      const parsedToken = parseGetSrcProfileIdToken(token);
      const amr = (parsedToken.amr || []).map(v => {
        if (typeof v === 'string') {
          return v.toLowerCase();
        }

        return v;
      });
      const iss = (parsedToken.iss || '').toString().toLowerCase();

      if (!iss.includes('visa') && (amr.includes('swk') || amr.includes('cookie'))) {
        const baseProps = getBaseTrackRecognizedProps(config);
        const baseTrackRecognizedProps = {
          ...baseProps,
          auth_provider: convertIssToCardBrand(iss),
          nvr: true,
          src_email_mask: parsedToken?.src_email_mask ?? baseProps.src_email_mask,
          user_type_identification: 'Recognized user',
          vat: false,
          vr: false
        };
        const eventName = 'User type identification';
        const gtmData = {
          api_name: 'getSrcProfile',
          event: eventName,
          event_action: eventName,
          event_label: `${eventName}-API`,
          ...baseTrackRecognizedProps
        };

        pushGtmData({
          ...gtmData
        });

        PostMessage.send({
          data: {
            ...baseTrackRecognizedProps
          },
          type: SYS_SYNC_GTM_DATA_EVENT
        });
      }
    }
  }
};

const trackVisaUnrecognizedReturningUser = ({
  accessToken = '',
  apiName = 'completeIdentityValidation',
  config = {},
  idTokens = [],
  initData = {}
} = {}) => {
  if (!initData.visaSrci) {
    let parsedToken;
    if (Array.isArray(idTokens) || (typeof idTokens === 'string' && idTokens.length > 0)) {
      let token = idTokens;
      if (Array.isArray(idTokens)) {
        token = idTokens[0]?.token ?? idTokens[0];
      }

      parsedToken = parseGetSrcProfileIdToken(token);
    }

    const baseTrackRecognizedProps = {
      ...getBaseTrackRecognizedProps(config),
      auth_provider: VISA,
      nvr: false,
      src_email_mask: parsedToken?.src_email_mask ?? 'undefined',
      user_type_identification: 'Unrecognized Returning user',
      vat: Boolean(accessToken),
      vr: false
    };
    const eventName = 'User type identification';
    const gtmData = {
      api_name: apiName,
      event: eventName,
      event_action: eventName,
      event_label: `${eventName}-API`,
      ...baseTrackRecognizedProps
    };

    pushGtmData({
      ...gtmData
    });

    PostMessage.send({
      data: {
        ...baseTrackRecognizedProps
      },
      type: SYS_SYNC_GTM_DATA_EVENT
    });
  }
};

const trackNonVisaUnrecognizedReturningUser = ({
  accessToken = '',
  config = {},
  idTokens = [],
  initData = {}
} = {}) => {
  if (!initData.visaSrci) {
    if (
      Array.isArray(idTokens?.idTokens) ||
      Array.isArray(idTokens) ||
      (typeof idTokens === 'string' && idTokens.length > 0)
    ) {
      let token = idTokens;
      if (Array.isArray(idTokens)) {
        token = idTokens[0];
      } else if (Array.isArray(idTokens.idTokens)) {
        token = idTokens.idTokens[0];
      }

      const parsedToken = parseGetSrcProfileIdToken(token);
      const amr = (parsedToken.amr || []).map(v => {
        if (typeof v === 'string') {
          return v.toLowerCase();
        }

        return v;
      });
      const iss = (parsedToken.iss || '').toString().toLowerCase();

      if (!iss.includes('visa') && (amr.includes('email_otp') || amr.includes('sms_otp'))) {
        const baseProps = getBaseTrackRecognizedProps(config);
        const baseTrackRecognizedProps = {
          ...baseProps,
          auth_provider: convertIssToCardBrand(iss),
          nvr: true,
          src_email_mask: parsedToken?.src_email_mask ?? baseProps.src_email_mask,
          user_type_identification: 'Unrecognized Returning user',
          vat: Boolean(accessToken),
          vr: false
        };
        const eventName = 'User type identification';
        const gtmData = {
          api_name: 'getSrcProfile',
          event: eventName,
          event_action: eventName,
          event_label: `${eventName}-API`,
          ...baseTrackRecognizedProps
        };

        pushGtmData({
          ...gtmData
        });

        PostMessage.send({
          data: {
            ...baseTrackRecognizedProps
          },
          type: SYS_SYNC_GTM_DATA_EVENT
        });
      }
    }
  }
};

const GtmHelper = {
  USER_FLOW,
  pushGtmData,
  trackCampaignCookieAttributes,
  trackNonVisaRecognizedUser,
  trackNonVisaSRCiCustomAttributes,
  trackNonVisaUnrecognizedReturningUser,
  trackSetIntentCalled,
  trackTransactionAndOrderType,
  trackVisaRecognizedUser,
  trackVisaUnrecognizedReturningUser
};

export default GtmHelper;
